import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarArrowUp,
  Loader2,
  MessageSquare,
  Target,
  Trash2,
  BookOpen,
  Search,
  X,
  ChevronsDownUp,
  ChevronsUpDown,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTasks } from '../hooks/useTasks';
import { useDayFocus } from '../hooks/useDayFocus';
import { TaskCard } from '../components/tasks/TaskCard';
import ConversationSessionCard, {
  getConversationSessionDate,
  getConversationSessionTitle,
} from '../components/session/ConversationSessionCard';
import { JournalDayView } from '../components/journal/JournalDayView';
import { useTimelineSessions } from '../hooks/useTimelineSessions';
import { deleteSession, updateSession, type DeleteSessionResult } from '../services/sessions';
import { callProcessExtraction } from '../services/functions';
import { getTodayDateKey } from '../services/dayFocus';
import { brandColors } from '@/design';
import type { DayFocus, Session, TaskPlan } from '../types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type TodayTab = 'tasks' | 'journal' | 'conversations';

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getTaskDate(task: TaskPlan): Date {
  if (task.createdAt?.toDate) return task.createdAt.toDate();
  return new Date((task.createdAt as unknown as { seconds: number }).seconds * 1000);
}

function isActionItemComplete(item: TaskPlan['actionItems'][number]): boolean {
  if (typeof item.status === 'string') return item.status === 'done';
  return item.completed;
}

function isTaskListFullyCompleted(task: TaskPlan): boolean {
  if (task.status === 'completed') return true;
  if (task.actionItems.length === 0) return false;
  return task.actionItems.every(isActionItemComplete);
}

function getDayFocusDate(focus: DayFocus): Date {
  if (focus.createdAt?.toDate) return focus.createdAt.toDate();
  return new Date((focus.createdAt as unknown as { seconds: number }).seconds * 1000);
}

function dayFocusToTaskPlan(focus: DayFocus): TaskPlan {
  return {
    id: focus.id,
    userId: focus.userId,
    sessionId: focus.sessionId,
    title: focus.title,
    summary: focus.summary ?? '',
    status: focus.status,
    actionItems: focus.actionItems,
    createdAt: focus.createdAt,
    updatedAt: focus.updatedAt,
    completedAt: focus.completedAt,
  };
}

type PendingDeletion =
  | { type: 'session'; sessionId: string; title: string; reason?: 'no_transcript' }
  | { type: 'task'; taskId: string; title: string }
  | { type: 'day_focus'; focusId: string; title: string };

export default function TodayTasksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { allTasks, loading, toggle, addItem, deleteItem, reorder, deleteTask } = useTasks(user?.uid ?? null);
  const {
    allFocuses,
    todayFocuses,
    tomorrowFocuses,
    loading: dayFocusLoading,
    toggle: toggleFocusItem,
    addItem: addFocusItem,
    deleteItem: deleteFocusItem,
    reorder: reorderFocusItems,
    deleteFocus,
    moveFocusToDate,
  } = useDayFocus(user?.uid ?? null);
  const [showAllTasks, setShowAllTasks] = useState(() => searchParams.get('showAll') === 'true');
  const [hideCompletedLists, setHideCompletedLists] = useState(true);
  const [todayTab, setTodayTab] = useState<TodayTab>('tasks');
  const today = useMemo(() => startOfDay(new Date()), []);
  const tomorrow = useMemo(() => {
    const end = new Date(today);
    end.setDate(end.getDate() + 1);
    end.setHours(0, 0, 0, 0);
    return end;
  }, [today]);

  const { sessionsByDay, loading: sessionsLoading, refetch: refetchSessions } = useTimelineSessions(user?.uid ?? null, today, tomorrow);
  const todayKey = today.toISOString();
  // YYYY-MM-DD string used for focusDate comparisons and moveFocusToDate
  const todayDateKey = useMemo(() => getTodayDateKey(), []);

  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteFeedback, setDeleteFeedback] = useState<string | null>(null);
  const [analyzingSessionId, setAnalyzingSessionId] = useState<string | null>(null);
  const [movingFocusId, setMovingFocusId] = useState<string | null>(null);
  const isErrorFeedback = Boolean(deleteFeedback?.startsWith('Error'));

  // Search + collapse state for showAllTasks view
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchClosing, setSearchClosing] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchCloseTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setShowAllTasks(searchParams.get('showAll') === 'true');
  }, [searchParams]);

  useEffect(() => {
    if (showAllTasks) {
      setHideCompletedLists(true);
    }
  }, [showAllTasks]);

  useEffect(() => {
    if (!deleteFeedback) return;
    const timer = setTimeout(() => setDeleteFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [deleteFeedback]);

  useEffect(() => {
    return () => {
      if (searchCloseTimerRef.current) {
        window.clearTimeout(searchCloseTimerRef.current);
      }
    };
  }, []);

  async function handleConfirmDelete() {
    if (!user || !pendingDeletion) return;
    setDeleting(true);
    try {
      if (pendingDeletion.type === 'session') {
        const result: DeleteSessionResult = await deleteSession(user.uid, pendingDeletion.sessionId);
        const parts: string[] = [];
        if (result.nodesDeleted > 0) parts.push(`${result.nodesDeleted} belief${result.nodesDeleted !== 1 ? 's' : ''}`);
        if (result.edgesDeleted > 0) parts.push(`${result.edgesDeleted} connection${result.edgesDeleted !== 1 ? 's' : ''}`);
        if (result.motifsDeleted > 0) parts.push(`${result.motifsDeleted} pattern${result.motifsDeleted !== 1 ? 's' : ''}`);
        if (result.tasksDeleted > 0) parts.push(`${result.tasksDeleted} task${result.tasksDeleted !== 1 ? 's' : ''}`);
        if (result.dayFocusDeleted > 0) parts.push(`${result.dayFocusDeleted} day focus${result.dayFocusDeleted !== 1 ? 'es' : ''}`);
        const cleanupNote = parts.length > 0 ? ` Cleaned up ${parts.join(', ')}.` : '';
        setDeleteFeedback(`Deleted "${pendingDeletion.title}".${cleanupNote}`);
        refetchSessions();
      } else if (pendingDeletion.type === 'task') {
        await deleteTask(pendingDeletion.taskId);
        setDeleteFeedback(`Deleted task "${pendingDeletion.title}".`);
      } else {
        await deleteFocus(pendingDeletion.focusId);
        setDeleteFeedback(`Deleted day focus "${pendingDeletion.title}".`);
      }
    } catch (err) {
      setDeleteFeedback(`Error: ${err instanceof Error ? err.message : 'Failed to delete'}`);
    } finally {
      setDeleting(false);
      setPendingDeletion(null);
    }
  }

  async function handleAnalyzeActiveSession(session: Session) {
    if (!user || session.status !== 'active') return;
    setAnalyzingSessionId(session.id);
    try {
      await callProcessExtraction(session.id, {});
      setDeleteFeedback(`Finished analysis for "${getConversationSessionTitle(session)}".`);
      refetchSessions();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to analyze conversation';
      const noTranscript = /no messages to extract from/i.test(message);
      if (noTranscript) {
        try {
          await updateSession(user.uid, session.id, { status: 'abandoned' });
        } catch {
          // Best effort — backend also marks this case abandoned.
        }
        setDeleteFeedback('No transcript was captured. This session was marked Abandoned.');
        setPendingDeletion({
          type: 'session',
          sessionId: session.id,
          title: getConversationSessionTitle(session),
          reason: 'no_transcript',
        });
      } else {
        setDeleteFeedback(`Error: ${message}`);
      }
      refetchSessions();
    } finally {
      setAnalyzingSessionId(null);
    }
  }

  async function handleMoveFocusToToday(focusId: string) {
    setMovingFocusId(focusId);
    try {
      await moveFocusToDate(focusId, todayDateKey);
      setDeleteFeedback('Moved to today\'s tasks.');
    } catch (err) {
      setDeleteFeedback(`Error: ${err instanceof Error ? err.message : 'Failed to move task'}`);
    } finally {
      setMovingFocusId(null);
    }
  }

  const todayTasks = useMemo(() => {
    const filtered = allTasks.filter((task) => isSameDay(getTaskDate(task), today));
    return filtered.sort((a, b) => {
      const aComplete = a.status === 'completed' ? 1 : 0;
      const bComplete = b.status === 'completed' ? 1 : 0;
      if (aComplete !== bComplete) return aComplete - bComplete;
      return getTaskDate(b).getTime() - getTaskDate(a).getTime();
    });
  }, [allTasks, today]);

  const todayFocusTaskPlans = useMemo(
    () => todayFocuses.map(dayFocusToTaskPlan),
    [todayFocuses]
  );

  const tomorrowFocusTaskPlans = useMemo(
    () => tomorrowFocuses.map(dayFocusToTaskPlan),
    [tomorrowFocuses]
  );

  const todaySessions = useMemo(() => {
    const sessions = sessionsByDay.get(todayKey) ?? [];
    return [...sessions].sort((a, b) => getConversationSessionDate(b).getTime() - getConversationSessionDate(a).getTime());
  }, [sessionsByDay, todayKey]);

  const allTasksSorted = useMemo(
    () => [...allTasks].sort((a, b) => getTaskDate(b).getTime() - getTaskDate(a).getTime()),
    [allTasks]
  );

  const allFocusesSorted = useMemo(
    () => [...allFocuses].sort((a, b) => getDayFocusDate(b).getTime() - getDayFocusDate(a).getTime()),
    [allFocuses]
  );
  const allItemsSorted = useMemo(() => {
    return [
      ...allFocusesSorted.map((focus) => ({
        kind: 'day_focus' as const,
        id: focus.id,
        date: getDayFocusDate(focus),
        task: dayFocusToTaskPlan(focus),
        priority: focus.priority,
      })),
      ...allTasksSorted.map((task) => ({
        kind: 'task' as const,
        id: task.id,
        date: getTaskDate(task),
        task,
        priority: null,
      })),
    ].sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [allFocusesSorted, allTasksSorted]);
  const visibleAllItems = useMemo(() => {
    const base = hideCompletedLists
      ? allItemsSorted.filter((item) => !isTaskListFullyCompleted(item.task))
      : allItemsSorted;
    if (!searchQuery.trim()) return base;
    const q = searchQuery.trim().toLowerCase();
    return base.filter((item) => {
      const t = item.task;
      if (t.title.toLowerCase().includes(q)) return true;
      if (t.summary?.toLowerCase().includes(q)) return true;
      if (t.actionItems.some((ai) => ai.text.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [allItemsSorted, hideCompletedLists, searchQuery]);

  const allExpanded = visibleAllItems.length > 0 && visibleAllItems.every((item) => expandedIds.has(item.id));
  const searchVisible = showAllTasks || todayTab === 'tasks';
  const SEARCH_CLOSE_ANIMATION_MS = 280;
  const searchExpanded = searchOpen || searchClosing;

  const openSearch = useCallback(() => {
    if (searchCloseTimerRef.current) {
      window.clearTimeout(searchCloseTimerRef.current);
      searchCloseTimerRef.current = null;
    }
    setSearchClosing(false);
    setSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    if (!searchOpen && !searchClosing) return;
    if (searchCloseTimerRef.current) {
      window.clearTimeout(searchCloseTimerRef.current);
    }
    setSearchOpen(false);
    setSearchClosing(true);
    searchCloseTimerRef.current = window.setTimeout(() => {
      setSearchClosing(false);
      setSearchQuery('');
      searchCloseTimerRef.current = null;
    }, SEARCH_CLOSE_ANIMATION_MS);
  }, [searchClosing, searchOpen]);

  useEffect(() => {
    if (!searchOpen || !searchVisible) return undefined;
    const rafId = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [searchOpen, searchVisible]);

  // Filtered items for the today-only tasks tab
  const filteredTodayFocusTaskPlans = useMemo(() => {
    if (!searchQuery.trim()) return todayFocusTaskPlans;
    const q = searchQuery.trim().toLowerCase();
    return todayFocusTaskPlans.filter((t) =>
      t.title.toLowerCase().includes(q) ||
      t.summary?.toLowerCase().includes(q) ||
      t.actionItems.some((ai) => ai.text.toLowerCase().includes(q))
    );
  }, [todayFocusTaskPlans, searchQuery]);

  const filteredTomorrowFocusTaskPlans = useMemo(() => {
    if (!searchQuery.trim()) return tomorrowFocusTaskPlans;
    const q = searchQuery.trim().toLowerCase();
    return tomorrowFocusTaskPlans.filter((t) =>
      t.title.toLowerCase().includes(q) ||
      t.summary?.toLowerCase().includes(q) ||
      t.actionItems.some((ai) => ai.text.toLowerCase().includes(q))
    );
  }, [tomorrowFocusTaskPlans, searchQuery]);

  // Unified list: today's focuses first, then tomorrow's (evening planned tasks)
  const filteredActiveFocusTaskPlans = useMemo(
    () => [...filteredTodayFocusTaskPlans, ...filteredTomorrowFocusTaskPlans],
    [filteredTodayFocusTaskPlans, filteredTomorrowFocusTaskPlans]
  );

  const tomorrowFocusIds = useMemo(
    () => new Set(tomorrowFocusTaskPlans.map((t) => t.id)),
    [tomorrowFocusTaskPlans]
  );

  // IDs of focuses that are "current" (today or tomorrow) — anything else in allFocuses is a past-day focus
  const activeFocusIds = useMemo(
    () => new Set([
      ...todayFocusTaskPlans.map((t) => t.id),
      ...tomorrowFocusTaskPlans.map((t) => t.id),
    ]),
    [todayFocusTaskPlans, tomorrowFocusTaskPlans]
  );

  const filteredTodayTasks = useMemo(() => {
    if (!searchQuery.trim()) return todayTasks;
    const q = searchQuery.trim().toLowerCase();
    return todayTasks.filter((t) =>
      t.title.toLowerCase().includes(q) ||
      t.summary?.toLowerCase().includes(q) ||
      t.actionItems.some((ai) => ai.text.toLowerCase().includes(q))
    );
  }, [todayTasks, searchQuery]);

  const todayTaskIds = useMemo(
    () => [
      ...todayFocusTaskPlans.map((t) => t.id),
      ...tomorrowFocusTaskPlans.map((t) => t.id),
      ...todayTasks.map((t) => t.id),
    ],
    [todayFocusTaskPlans, tomorrowFocusTaskPlans, todayTasks]
  );

  const allTodayExpanded = todayTaskIds.length > 0 && todayTaskIds.every((id) => expandedIds.has(id));

  function toggleExpandAll() {
    if (showAllTasks) {
      if (allExpanded) {
        setExpandedIds(new Set());
      } else {
        setExpandedIds(new Set(visibleAllItems.map((item) => item.id)));
      }
    } else {
      if (allTodayExpanded) {
        setExpandedIds(new Set());
      } else {
        setExpandedIds(new Set(todayTaskIds));
      }
    }
  }

  function toggleCardExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const tasksPanelLoading = loading || dayFocusLoading;

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <div
        className="sticky top-0 z-40"
        style={{
          background: 'var(--color-background)',
          borderBottom: '1px solid var(--color-border)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <header className="px-5 pt-5 pb-3 max-w-2xl lg:max-w-5xl mx-auto flex items-center gap-3 relative">
          <div
            className="flex items-center gap-3 shrink-0"
            style={searchExpanded ? { position: 'relative', zIndex: 20 } : undefined}
          >
            <button
              onClick={() => navigate('/')}
              className="p-2 -ml-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1
              className={`text-xl font-bold text-[var(--color-text-primary)] transition-opacity duration-300 ${
                searchExpanded ? 'opacity-0' : 'opacity-100'
              }`}
            >
              Today's Activities
            </h1>
          </div>

          <div
            className={`flex items-center gap-2 ${
              searchExpanded
                ? 'absolute inset-0 z-10 px-5 justify-end'
                : 'flex-1 justify-end'
            }`}
          >
            {/* Search — visible when tasks tab is active (both modes) */}
            {searchVisible && (
              <div
                className={`flex items-center justify-end transition-all duration-300 ease-out ${
                  searchExpanded ? 'flex-1' : 'flex-none'
                }`}
              >
                <button
                  onClick={openSearch}
                  className={`inline-flex items-center justify-center h-9 rounded-lg transition-[width,opacity,transform,background-color] duration-300 ${
                    searchExpanded
                      ? 'w-0 opacity-0 scale-95 pointer-events-none'
                      : 'w-9 opacity-100 scale-100'
                  }`}
                  style={{
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-secondary)',
                    border: '1px solid var(--color-border)',
                  }}
                  title="Search tasks"
                  aria-label="Search tasks"
                >
                  <Search size={15} />
                </button>
                <div
                  className={`flex items-center gap-1.5 rounded-lg h-9 min-w-0 overflow-hidden transition-[max-width,opacity,transform,padding,border-color] duration-300 ease-out ${
                    searchExpanded
                      ? 'flex-1 opacity-100 scale-100 px-2 border'
                      : 'max-w-0 opacity-0 scale-95 px-0 border border-transparent'
                  }`}
                  style={{
                    background: 'var(--color-surface)',
                    borderColor: searchExpanded ? 'var(--color-border)' : 'transparent',
                    maxWidth: searchExpanded ? '100%' : '0px',
                  }}
                >
                  <Search size={13} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tasks…"
                    className="flex-1 bg-transparent text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none min-w-0"
                  />
                  <button
                    onClick={closeSearch}
                    className="shrink-0 hover:opacity-70 transition-opacity"
                    aria-label="Close search"
                  >
                    <X size={13} style={{ color: 'var(--color-text-muted)' }} />
                  </button>
                </div>
              </div>
            )}

            {/* Expand/collapse all — visible when tasks tab is active (both modes) */}
            {(showAllTasks || todayTab === 'tasks') && (
              <button
                onClick={toggleExpandAll}
                className="p-2 rounded-lg transition-colors"
                style={{
                  background: 'var(--color-surface)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                }}
                title={showAllTasks ? (allExpanded ? 'Collapse all' : 'Expand all') : (allTodayExpanded ? 'Collapse all' : 'Expand all')}
              >
                {(showAllTasks ? allExpanded : allTodayExpanded)
                  ? <ChevronsDownUp size={15} />
                  : <ChevronsUpDown size={15} />
                }
              </button>
            )}

            {/* Hide completed toggle — showAllTasks only */}
            {showAllTasks && (
              <button
                onClick={() => setHideCompletedLists((prev) => !prev)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
                style={{
                  background: hideCompletedLists ? `${brandColors.lightBlue}22` : 'var(--color-surface)',
                  color: hideCompletedLists ? brandColors.lightBlue : 'var(--color-text-secondary)',
                  border: `1px solid ${hideCompletedLists ? `${brandColors.lightBlue}66` : 'var(--color-border)'}`,
                }}
              >
                {hideCompletedLists ? 'Show Completed' : 'Hide Completed'}
              </button>
            )}

            <button
              onClick={() => {
                const next = !showAllTasks;
                setShowAllTasks(next);
                if (!next) { closeSearch(); setExpandedIds(new Set()); }
                const nextParams = new URLSearchParams(searchParams);
                if (next) nextParams.set('showAll', 'true');
                else nextParams.delete('showAll');
                setSearchParams(nextParams, { replace: true });
              }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
              style={{
                background: 'var(--color-surface)',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)',
              }}
            >
              {showAllTasks ? 'Hide All Tasks' : 'Show All Tasks'}
            </button>
          </div>
        </header>
      </div>

      <div className="px-5 pb-10 max-w-2xl lg:max-w-5xl mx-auto flex flex-col gap-4">
        {!showAllTasks && (
          <div className="flank-card rounded-xl p-1 grid grid-cols-3 gap-1">
            <button
              onClick={() => setTodayTab('tasks')}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                todayTab === 'tasks' ? 'text-white' : 'text-[var(--color-text-secondary)]'
              }`}
              style={{ background: todayTab === 'tasks' ? brandColors.darkBlue : 'transparent' }}
            >
              Tasks
            </button>
            <button
              onClick={() => setTodayTab('journal')}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                todayTab === 'journal' ? 'text-white' : 'text-[var(--color-text-secondary)]'
              }`}
              style={{ background: todayTab === 'journal' ? brandColors.darkBlue : 'transparent' }}
            >
              <BookOpen size={14} />
              Journal
            </button>
            <button
              onClick={() => setTodayTab('conversations')}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                todayTab === 'conversations' ? 'text-white' : 'text-[var(--color-text-secondary)]'
              }`}
              style={{ background: todayTab === 'conversations' ? brandColors.darkBlue : 'transparent' }}
            >
              Conversations
            </button>
          </div>
        )}

        {(showAllTasks || todayTab === 'tasks') && tasksPanelLoading && (
          <div className="py-12 flex justify-center">
            <div className="w-6 h-6 rounded-full animate-pulse" style={{ background: 'var(--color-surface)' }} />
          </div>
        )}

        {!showAllTasks && todayTab === 'conversations' && sessionsLoading && (
          <div className="py-12 flex justify-center">
            <div className="w-6 h-6 rounded-full animate-pulse" style={{ background: 'var(--color-surface)' }} />
          </div>
        )}

        {!tasksPanelLoading && !showAllTasks && todayTab === 'tasks' && todayTasks.length === 0 && filteredActiveFocusTaskPlans.length === 0 && (
          <div className="py-12 text-center">
            <Target size={32} className="mx-auto mb-3 opacity-30" style={{ color: 'var(--color-text-muted)' }} />
            <p className="text-sm text-[var(--color-text-muted)]">No tasks for today</p>
          </div>
        )}

        {!sessionsLoading && !showAllTasks && todayTab === 'conversations' && todaySessions.length === 0 && (
          <div className="py-12 text-center">
            <MessageSquare size={32} className="mx-auto mb-3 opacity-30" style={{ color: 'var(--color-text-muted)' }} />
            <p className="text-sm text-[var(--color-text-muted)]">No conversations today</p>
          </div>
        )}

        {!showAllTasks && todayTab === 'journal' && (
          <JournalDayView userId={user?.uid ?? null} date={today} />
        )}

        {!tasksPanelLoading && showAllTasks && visibleAllItems.length === 0 && (
          <div className="py-12 text-center">
            <Target size={32} className="mx-auto mb-3 opacity-30" style={{ color: 'var(--color-text-muted)' }} />
            <p className="text-sm text-[var(--color-text-muted)]">
              {allItemsSorted.length === 0 ? 'No tasks yet' : 'No active task lists'}
            </p>
            {allItemsSorted.length === 0 && (
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                Start a Task Planning session to create your first action plan
              </p>
            )}
          </div>
        )}

        {!showAllTasks && todayTab === 'tasks' && filteredActiveFocusTaskPlans.map((task) => (
          <TaskCard
            key={`focus-${task.id}`}
            task={task}
            variant="dayFocus"
            badgeLabel={tomorrowFocusIds.has(task.id) ? 'Tomorrow' : undefined}
            onToggle={toggleFocusItem}
            onAddItem={addFocusItem}
            onDeleteItem={deleteFocusItem}
            onReorder={reorderFocusItems}
            collapsed={!expandedIds.has(task.id)}
            onToggleCollapse={() => toggleCardExpanded(task.id)}
          />
        ))}

        {!showAllTasks && todayTab === 'tasks' && filteredTodayTasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onToggle={toggle}
            onAddItem={addItem}
            onDeleteItem={deleteItem}
            onReorder={reorder}
            collapsed={!expandedIds.has(task.id)}
            onToggleCollapse={() => toggleCardExpanded(task.id)}
          />
        ))}

        {!showAllTasks && todayTab === 'conversations' && todaySessions.map((session) => (
          <ConversationSessionCard
            key={session.id}
            session={session}
            onOpen={(sessionId) => navigate(`/conversations/${sessionId}`)}
            onAnalyze={() => handleAnalyzeActiveSession(session)}
            isAnalyzing={analyzingSessionId === session.id}
            onDelete={() => setPendingDeletion({
              type: 'session',
              sessionId: session.id,
              title: getConversationSessionTitle(session),
            })}
          />
        ))}

        {showAllTasks && (
          <div className="flex flex-col gap-4">
            {visibleAllItems.map((item) => {
              const isPastFocus = item.kind === 'day_focus' && !activeFocusIds.has(item.id);
              const isMoving = movingFocusId === item.id;
              return (
                <div key={`${item.kind}-${item.id}`}>
                  <TaskCard
                    task={item.task}
                    onToggle={item.kind === 'day_focus' ? toggleFocusItem : toggle}
                    onAddItem={item.kind === 'day_focus' ? addFocusItem : addItem}
                    onDeleteItem={item.kind === 'day_focus' ? deleteFocusItem : deleteItem}
                    onReorder={item.kind === 'day_focus' ? reorderFocusItems : reorder}
                    variant={item.kind === 'day_focus' ? 'dayFocus' : 'task'}
                    dateBadge={{ day: item.date.getDate(), month: MONTHS[item.date.getMonth()] }}
                    collapsed={!expandedIds.has(item.id)}
                    onToggleCollapse={() => toggleCardExpanded(item.id)}
                  />
                  {isPastFocus && (
                    <button
                      onClick={() => handleMoveFocusToToday(item.id)}
                      disabled={isMoving}
                      className="mt-1 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-colors"
                      style={{
                        background: isMoving ? 'var(--color-surface)' : `${brandColors.teal}12`,
                        color: isMoving ? 'var(--color-text-muted)' : brandColors.teal,
                        border: `1px solid ${isMoving ? 'var(--color-border)' : `${brandColors.teal}44`}`,
                        opacity: isMoving ? 0.7 : 1,
                      }}
                    >
                      {isMoving
                        ? <Loader2 size={12} className="animate-spin" />
                        : <CalendarArrowUp size={12} />
                      }
                      {isMoving ? 'Moving…' : 'Move to Today'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {deleteFeedback && (
          <div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 max-w-sm px-4 py-3 rounded-xl text-sm shadow-lg z-50"
            style={{
              background: isErrorFeedback
                ? 'var(--color-error)'
                : 'var(--color-success-subtle)',
              color: isErrorFeedback
                ? 'white'
                : 'var(--color-success)',
              border: `1px solid ${isErrorFeedback ? 'var(--color-error)' : 'var(--color-success)'}`,
              boxShadow: isErrorFeedback
                ? '0 10px 28px rgba(0, 0, 0, 0.35)'
                : '0 10px 28px rgba(0, 0, 0, 0.28)',
            }}
            onClick={() => setDeleteFeedback(null)}
          >
            {deleteFeedback}
          </div>
        )}
      </div>

      {pendingDeletion && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <div
            className="w-full max-w-sm rounded-2xl p-6 flex flex-col gap-4"
            style={{ background: 'var(--color-background)' }}
          >
            <div>
              <h3 className="font-semibold text-[var(--color-text-primary)]">
                {pendingDeletion.type === 'session'
                  ? (pendingDeletion.reason === 'no_transcript' ? 'Delete empty conversation?' : 'Delete conversation?')
                  : pendingDeletion.type === 'day_focus'
                    ? 'Delete day focus?'
                    : 'Delete task?'}
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                {pendingDeletion.type === 'session'
                  ? pendingDeletion.reason === 'no_transcript'
                    ? `No transcript was captured for "${pendingDeletion.title}". Delete this empty conversation now?`
                    : `Delete "${pendingDeletion.title}" and undo its contributions to your belief system?`
                  : `Delete "${pendingDeletion.title}" and all its action items?`
                }
              </p>
              {pendingDeletion.type === 'session' && (
                <p className="text-xs text-[var(--color-text-muted)] mt-2">
                  {pendingDeletion.reason === 'no_transcript'
                    ? 'Keeping it is safe too: it will remain marked as abandoned.'
                    : (
                      <>
                        This removes beliefs, connections, and patterns that were only established in this conversation.
                        Data reinforced by other sessions will be preserved.
                      </>
                    )}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingDeletion(null)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors"
                style={{
                  background: 'var(--color-surface)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {pendingDeletion.type === 'session' && pendingDeletion.reason === 'no_transcript'
                  ? 'Keep as abandoned'
                  : 'Cancel'}
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                style={{
                  background: 'var(--color-error)',
                  color: 'white',
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



